import { ArrowRight, Wallet, X } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { CARD_WALLET_SETTINGS_PATH } from "@/modules/dashboard/settings/constants/cardWalletSection";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import useTranslations from "@/shared/lang/useTranslations";
import { colocaToast } from "@/shared/utils/toast";

const DISMISS_KEY_PREFIX = "coloca:card-wallet-invite-dismissed:";

function readDismissed(organizationId: string | null): boolean {
  if (!organizationId) return false;
  try {
    return window.localStorage.getItem(`${DISMISS_KEY_PREFIX}${organizationId}`) === "1";
  } catch {
    return false;
  }
}

function writeDismissed(organizationId: string | null): void {
  if (!organizationId) return;
  try {
    window.localStorage.setItem(`${DISMISS_KEY_PREFIX}${organizationId}`, "1");
  } catch {
    // Storage refused (private window): the card comes back next visit, which is harmless.
  }
}

export interface CardWalletInviteCardProps {
  organizationId: string | null;
  onLearnMore: () => void;
}

/**
 * Where the card wallet banner would be, for an organization without one: a quiet line saying it exists and
 * what it is for, dismissible per organization in this browser. Never a balance — there is none to show.
 * Dismissing it says where the wallet can still be turned on (Settings), so hiding the line never hides the
 * way.
 */
export function CardWalletInviteCard({ organizationId, onLearnMore }: CardWalletInviteCardProps) {
  const { t } = useTranslations("cards");
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(() => readDismissed(organizationId));

  const dismiss = () => {
    writeDismissed(organizationId);
    setDismissed(true);
    colocaToast.info(t("card_wallet_invite_dismissed_title"), {
      description: t("card_wallet_invite_dismissed_body"),
      action: {
        label: t("card_wallet_invite_dismissed_action"),
        onClick: () => navigate(CARD_WALLET_SETTINGS_PATH),
      },
      duration: 8000,
    });
  };

  if (dismissed) return null;

  return (
    <section className="flex flex-wrap items-center gap-3 rounded-2xl border border-dashed border-border bg-muted/40 p-4">
      <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-card text-primary ring-1 ring-border">
        <Wallet aria-hidden="true" className="size-5" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          {t("card_wallet_invite_eyebrow")}
        </p>
        <p className="text-sm font-medium text-foreground">{t("card_wallet_invite_title")}</p>
        <p className="text-xs text-muted-foreground">{t("card_wallet_invite_body")}</p>
      </div>
      <div className="flex items-center gap-1">
        <ColocaButton size="sm" variant="default" iconTrailing={ArrowRight} onClick={onLearnMore}>
          {t("card_wallet_invite_cta")}
        </ColocaButton>
        <ColocaIconButton
          icon={X}
          size="sm"
          variant="ghost"
          aria-label={t("card_wallet_invite_dismiss")}
          onClick={dismiss}
        />
      </div>
    </section>
  );
}
